import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon, ChatBubbleLeftRightIcon, BookOpenIcon, ShareIcon, CodeBracketIcon, XMarkIcon, Cog6ToothIcon, LinkIcon } from '@heroicons/react/24/outline';
import './Dashboard.css';

function UsageBar({ label, used, limit, note }) {
  const unlimited = limit === -1;
  const pct = unlimited || used === null ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const warn = pct >= 80;
  const crit = pct >= 100;

  return (
    <div className="usage-row">
      <div className="usage-row-label">
        <span>{label}</span>
        {unlimited
          ? <span className="usage-count">Unlimited</span>
          : note
          ? <span className="usage-count">up to {limit} per chatbot</span>
          : <span className={`usage-count ${crit ? 'crit' : warn ? 'warn' : ''}`}>{used} / {limit}</span>
        }
      </div>
      {!unlimited && !note && (
        <div className="usage-bar">
          <div
            className={`usage-bar-fill ${crit ? 'crit' : warn ? 'warn' : ''}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

function EmbedModal({ chatbot, onClose }) {
  const base = window.location.origin;
  const shareUrl = `${base}/chat/${chatbot.id}`;
  const embedSnippet = `<script src="${base}/api/embed/${chatbot.id}/widget.js"></script>`;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied!`));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Share &amp; Embed — {chatbot.name}</h2>
          <button className="modal-close" onClick={onClose}><XMarkIcon className="icon" /></button>
        </div>

        <div className="modal-section">
          <label>Public Chat URL</label>
          <p className="modal-hint">Share this link with anyone — no login required.</p>
          <div className="modal-code-row">
            <code>{shareUrl}</code>
            <button className="btn btn-primary btn-sm" onClick={() => copy(shareUrl, 'Link')}>Copy</button>
          </div>
          <a href={shareUrl} target="_blank" rel="noreferrer" className="modal-preview-link">Open preview →</a>
        </div>

        <div className="modal-section">
          <label>Embed on your website</label>
          <p className="modal-hint">Paste this tag before <code>&lt;/body&gt;</code> on any webpage. A floating chat button will appear in the bottom-right corner.</p>
          <div className="modal-code-row">
            <code>{embedSnippet}</code>
            <button className="btn btn-primary btn-sm" onClick={() => copy(embedSnippet, 'Snippet')}>Copy</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const [chatbots, setChatbots] = useState([]);
  const [allChatbots, setAllChatbots] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [embedBot, setEmbedBot] = useState(null);
  const [tab, setTab] = useState('mine'); // 'mine' | 'all'
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchChatbots();
    fetchUsage();
  }, []);

  useEffect(() => {
    if (tab === 'all' && user?.is_admin && allChatbots.length === 0) {
      fetchAllChatbots();
    }
  }, [tab]);

  const fetchChatbots = async () => {
    try {
      const response = await axios.get('/api/chatbots');
      setChatbots(response.data.chatbots);
    } catch (error) {
      toast.error('Failed to load chatbots');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllChatbots = async () => {
    try {
      const response = await axios.get('/api/admin/chatbots');
      setAllChatbots(response.data.chatbots);
    } catch {
      toast.error('Failed to load all chatbots');
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await axios.get('/api/usage/me');
      setUsage(response.data);
    } catch {
      // non-critical
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this chatbot?')) return;
    try {
      await axios.delete(`/api/chatbots/${id}`);
      toast.success('Chatbot deleted');
      fetchChatbots();
      // Refresh all-bots list if it was loaded
      if (allChatbots.length > 0) setAllChatbots(prev => prev.filter(c => c.id !== id));
    } catch {
      toast.error('Failed to delete chatbot');
    }
  };

  const visibleChatbots = tab === 'all' ? allChatbots : chatbots;

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <>
    {embedBot && <EmbedModal chatbot={embedBot} onClose={() => setEmbedBot(null)} />}
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <h1>AI Chat Creator</h1>
            <div className="header-actions">
              <span className="user-info">
                {user?.name} ({user?.plan} plan)
              </span>
              {user?.is_admin && (
                <Link to="/admin" className="btn btn-admin">
                  <Cog6ToothIcon className="icon" />
                  Admin Panel
                </Link>
              )}
              <button onClick={logout} className="btn btn-secondary">
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="container">
          {usage && (
            <div className="usage-card">
              <div className="usage-card-header">
                <span className={`plan-badge plan-${usage.plan}`}>{usage.plan_label} Plan</span>
                <span className="usage-month">{usage.month}</span>
              </div>
              <div className="usage-rows">
                <UsageBar
                  label="Messages this month"
                  used={usage.usage.messages_this_month}
                  limit={usage.limits.messages_per_month}
                />
                <UsageBar
                  label="Chatbots"
                  used={usage.usage.chatbots}
                  limit={usage.limits.chatbots}
                />
                <UsageBar
                  label="Docs per chatbot"
                  used={null}
                  limit={usage.limits.documents_per_chatbot}
                  note
                />
              </div>
            </div>
          )}

          <div className="dashboard-actions">
            <div className="dashboard-tabs">
              <button
                className={`tab-btn ${tab === 'mine' ? 'active' : ''}`}
                onClick={() => setTab('mine')}
              >
                My Chatbots
                <span className="tab-count">{chatbots.length}</span>
              </button>
              {user?.is_admin && (
                <button
                  className={`tab-btn ${tab === 'all' ? 'active' : ''}`}
                  onClick={() => setTab('all')}
                >
                  All Users' Bots
                  {allChatbots.length > 0 && <span className="tab-count">{allChatbots.length}</span>}
                </button>
              )}
            </div>
            <button
              onClick={() => navigate('/builder')}
              className="btn btn-primary"
            >
              <PlusIcon className="icon" />
              Create New Chatbot
            </button>
          </div>

          {visibleChatbots.length === 0 ? (
            <div className="empty-state">
              <ChatBubbleLeftRightIcon className="empty-icon" />
              <h3>{tab === 'all' ? 'No chatbots found' : 'No chatbots yet'}</h3>
              <p>{tab === 'all' ? 'No users have created any chatbots yet' : 'Create your first AI chatbot to get started'}</p>
              {tab === 'mine' && (
                <button onClick={() => navigate('/builder')} className="btn btn-primary">
                  Create Chatbot
                </button>
              )}
            </div>
          ) : (
            <div className="chatbots-grid">
              {visibleChatbots.map((chatbot) => (
                <div key={chatbot.id} className="chatbot-card">
                  <div className="chatbot-header">
                    <h3>{chatbot.name}</h3>
                    <div className="chatbot-status">
                      <span className={`status-badge ${chatbot.is_active ? 'active' : 'inactive'}`}>
                        {chatbot.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  {tab === 'all' && chatbot.owner_email && (
                    <div className="chatbot-owner">
                      <span className="owner-label">Owner:</span>
                      <span className="owner-name">{chatbot.owner_name || chatbot.owner_email}</span>
                      <span className={`plan-badge plan-${chatbot.owner_plan}`}>{chatbot.owner_plan}</span>
                    </div>
                  )}
                  {chatbot.description && (
                    <p className="chatbot-description">{chatbot.description}</p>
                  )}
                  <div className="chatbot-stats">
                    <div className="chatbot-stat">
                      <span className="stat-value">{chatbot.total_conversations ?? 0}</span>
                      <span className="stat-label">Conversations</span>
                    </div>
                    <div className="chatbot-stat">
                      <span className="stat-value">{chatbot.messages_this_month ?? 0}</span>
                      <span className="stat-label">Msgs this month</span>
                    </div>
                    <div className="chatbot-stat">
                      <span className="stat-value">{chatbot.total_messages ?? 0}</span>
                      <span className="stat-label">Total messages</span>
                    </div>
                    <div className="chatbot-stat">
                      <span className="stat-value">
                        {chatbot.last_active
                          ? new Date(chatbot.last_active).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                          : '—'}
                      </span>
                      <span className="stat-label">Last active</span>
                    </div>
                  </div>
                  <div className="chatbot-meta">
                    <span>Model: {chatbot.model_name}</span>
                    <span>Provider: {chatbot.model_provider}</span>
                  </div>
                  <div className="chatbot-actions">
                    <button
                      onClick={() => navigate(`/chatbot/${chatbot.id}`)}
                      className="btn btn-primary btn-sm"
                    >
                      <ChatBubbleLeftRightIcon className="icon" />
                      Test Chat
                    </button>
                    <button
                      onClick={() => setEmbedBot(chatbot)}
                      className="btn btn-share btn-sm"
                    >
                      <ShareIcon className="icon" />
                      Share
                    </button>
                    <button
                      onClick={() => navigate(`/knowledge/${chatbot.id}`)}
                      className="btn btn-secondary btn-sm"
                    >
                      <BookOpenIcon className="icon" />
                      Knowledge Base
                    </button>
                    <button
                      onClick={() => navigate(`/integrations/${chatbot.id}`)}
                      className="btn btn-secondary btn-sm"
                    >
                      <LinkIcon className="icon" />
                      Integrations
                    </button>
                    <button
                      onClick={() => navigate(`/builder/${chatbot.id}`)}
                      className="btn btn-secondary btn-sm"
                    >
                      <PencilIcon className="icon" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(chatbot.id)}
                      className="btn btn-danger btn-sm"
                    >
                      <TrashIcon className="icon" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
    </>
  );
}

export default Dashboard;



